import { post } from '@/api/client';
import { rememberDestination } from '@/auth/destination';
import { T } from '@/i18n';
import { recordPublicPage, track } from '@/marketing/analytics';
import { completeRegistration, registrationPageView } from '@/marketing/pixel';
import { Button } from '@/ui';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import DemoEntry from './DemoEntry';
import { SignInWith } from './providers';
import { Field, KontoBox, Unavailable, failureText, hint, passwordProblem, useAvailability } from './shared';

// the terms page (issue P2.3 vilkår): not written yet; set its address here and the line shows
const TERMS_URL: string | null = null;

/**
 * /registrer. The site's «Åpne gratisdemo» (?next=demo) gets its own page, DemoEntry (design of
 * 2026-10-12); every other visit is the ordinary sign-up below, unchanged.
 */
export default function Registrer() {
  const { search } = useLocation();
  // the site's ?next=demo or ?next=oppsett, kept through the confirmation mail (plan 0028 slice 1)
  useEffect(() => rememberDestination(search), [search]);
  return new URLSearchParams(search).get('next') === 'demo' ? <DemoEntry /> : <Signup />;
}

/** POST /account/signup; the answer is the same neutral 202 whether or not the address is known. */
function Signup() {
  const available = useAvailability();
  const { search } = useLocation();
  // where the visit came from, as the site put it in the link: the lead keeps it (leads.came_from)
  const utm = new URLSearchParams(search).get('utm_source') ?? '';
  const utm_content = new URLSearchParams(search).get('utm_content') ?? ''; // which ad, 2026-10-04
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [lastname, setLastname] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  // the registration page is one of the two places the Meta Pixel may run, with marketing consent
  useEffect(() => registrationPageView(), []);
  // replay of this public page, inputs masked (owner 2026-10-05)
  useEffect(() => recordPublicPage(), []);
  // the first real interaction, once (issue #15): not every keystroke
  const startedRef = useRef(false);
  const started = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    track('signup_started', { signup_method: 'email_password' });
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    const pp = passwordProblem(password);
    if (pp) return setErr(pp);
    setBusy(true);
    setErr(null);
    try {
      await post('/account/signup', { email, name, lastname, password, utm, utm_content });
      completeRegistration(); // the ad conversion, in the browser the ad was clicked in (pixel.ts)
      setSent(true);
    } catch (ex) {
      setErr(failureText(ex)); // the entered values stay
    } finally {
      setBusy(false);
    }
  }

  // say it is not open rather than collect a name and an address and refuse them afterwards
  if (available.signup === false) {
    return (
      <KontoBox title={T('Opprett konto')}>
        <Unavailable what={T('Registrering er ikke åpnet ennå.')} />
      </KontoBox>
    );
  }

  return (
    <KontoBox
      title={sent ? T('Sjekk innboksen din') : T('Opprett konto')}
      sub={sent ? undefined : T('Kom i gang med Timefly.')}
    >
      {sent ? (
        <div className="opt">
          <output className="al info ph-no-capture">
            {T(
              'Hvis adressen kan brukes, har vi sendt en e-post med en lenke for å bekrefte den. Sjekk innboksen.',
            )}
          </output>
          <Link to="/login" style={hint}>
            {T('Til innlogging')}
          </Link>
        </div>
      ) : (
        <form className="opt" onSubmit={submit} onFocus={started} style={{ display: 'grid', gap: 10 }}>
          {/* Google, Microsoft or Apple instead of the address and the confirmation mail (#191; the demo
              form has had them since 2026-09-30) */}
          {available.signInWith.length > 0 && (
            <SignInWith
              providers={available.signInWith}
              failed={new URLSearchParams(search).get('innlogging')}
              via={new URLSearchParams(search).get('via')}
              forCompany
            />
          )}
          <Field
            label={T('E-post')}
            type="email"
            autoComplete="email"
            placeholder={T('deg@bedrift.no')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Field
            label={T('Fornavn')}
            type="text"
            autoComplete="given-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Field
            label={T('Etternavn')}
            type="text"
            autoComplete="family-name"
            value={lastname}
            onChange={(e) => setLastname(e.target.value)}
            required
          />
          <Field
            label={T('Passord (minst 8 tegn)')}
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            last
          />
          {TERMS_URL && (
            <p className="auth-note">
              {T('Ved å trykke Opprett konto godtar du')}{' '}
              <a href={TERMS_URL} target="_blank" rel="noreferrer">
                {T('vilkårene')}
              </a>
              .
            </p>
          )}
          {err && <div className="al dgr">{err}</div>}
          <Button tone="pri" type="submit" disabled={busy}>
            {busy ? T('Sender …') : T('Opprett konto')}
          </Button>
        </form>
      )}
      {!sent && (
        <p className="auth-alt">
          {T('Har du allerede en konto?')} <Link to="/login">{T('Logg inn')}</Link>
        </p>
      )}
    </KontoBox>
  );
}
